import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Table, Pagination, StatusChip, Dropdown, type TableColumn, type DropdownOption } from '../../Common';
import { authService } from '../../../services';
import { toast } from 'react-toastify';
import './UsersTable.css';

export interface User {
  adminId: string;
  name: string;
  email: string;
  lastActive: string;
  status: 'active' | 'suspended' | 'pending' | null;
  role?: 'superadmin';
  isYou?: boolean;
}

interface UsersTableProps {
  users: User[];
  showPagination?: boolean;
  itemsPerPage?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onUserUpdated?: () => void;
  onEditUser?: (user: User) => void;
  currentUserEmail?: string;
  currentUserRole?: 'admin' | 'super_admin' | null;
}

const UsersTable: React.FC<UsersTableProps> = ({ 
  users,
  showPagination = true,
  itemsPerPage = 10,
  currentPage: controlledPage,
  onPageChange: controlledOnPageChange,
  onUserUpdated,
  onEditUser,
  currentUserEmail,
  currentUserRole,
}) => {
  // Internal state for uncontrolled pagination
  const [internalPage, setInternalPage] = useState(1);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const open = Boolean(anchorEl);
  
  // Check if current user is super admin
  const isSuperAdmin = currentUserRole === 'super_admin';
  
  // Use controlled or uncontrolled pagination
  const currentPage = controlledPage ?? internalPage;
  const onPageChange = controlledOnPageChange ?? setInternalPage;

  // Pagination calculations
  const totalPages = Math.ceil(users.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = showPagination ? users.slice(startIndex, endIndex) : users;

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleEdit = () => {
    if (selectedUser && onEditUser) {
      onEditUser(selectedUser);
    }
    handleMenuClose();
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;
    
    try {
      await authService.suspendAdmin(selectedUser.adminId);
      toast.success('User suspended successfully');
      handleMenuClose();
      
      // Refetch the users list to update the table
      if (onUserUpdated) {
        onUserUpdated();
      }
    } catch (error: any) {
      console.error('Failed to suspend user:', error);
      toast.error(error?.response?.data?.message || 'Failed to suspend user');
      handleMenuClose();
    }
  };

  const handleReactivate = async () => {
    if (!selectedUser) return;
    
    try {
      await authService.reactivateAdmin(selectedUser.adminId);
      toast.success('User reactivated successfully');
      handleMenuClose();
      
      // Refetch the users list to update the table
      if (onUserUpdated) {
        onUserUpdated();
      }
    } catch (error: any) {
      console.error('Failed to reactivate user:', error);
      toast.error(error?.response?.data?.message || 'Failed to reactivate user');
      handleMenuClose();
    }
  };

  // Dropdown options - different options based on user status
  const getDropdownOptions = (user: User | null): DropdownOption[] => {
    if (!user) return [];

    const isSuspended = user.status === 'suspended';
    const isPending = user.status === 'pending';
    const isUserSuperAdmin = user.role === 'superadmin';
    
    // No one can edit super admins
    const canEdit = !isUserSuperAdmin;
    
    // Only super admins can suspend/reactivate
    const canSuspendOrReactivate = isSuperAdmin;

    const options: DropdownOption[] = [
      {
        label: 'Edit',
        value: 'edit',
        onClick: handleEdit,
        disabled: !canEdit, // Disable edit for super admins
      },
    ];
    
    // Only add suspend/reactivate option if user is super admin
    if (canSuspendOrReactivate) {
      options.push({
        label: isSuspended ? 'Reactivate' : 'Suspend',
        value: isSuspended ? 'reactivate' : 'suspend',
        // Default for reactivate, secondary gray for pending suspend, danger red for active suspend
        color: isSuspended ? undefined : (isPending ? 'secondary' : 'danger'),
        disabled: isPending, // Disable suspend for pending users
        onClick: isSuspended ? handleReactivate : handleSuspend,
      });
    }
    
    return options;
  };

  const columns: TableColumn<User>[] = [
    {
      key: 'user',
      label: 'User',
      width: 1,
      render: (user: User) => {
        const isDisabled = user.status === 'suspended' || user.status === 'pending';
        return (
          <Box className="users-table-user-cell">
            <Typography className={isDisabled ? 'disabled' : ''}>
              {user.name}
            </Typography>
            {user.role === 'superadmin' && (
              <StatusChip status="superadmin" />
            )}
            {user.isYou && (
              <StatusChip status="you" />
            )}
          </Box>
        );
      },
    },
    {
      key: 'email',
      label: 'Email address',
      width: 1,
      render: (user: User) => {
        const isDisabled = user.status === 'suspended' || user.status === 'pending';
        return (
          <Typography className={isDisabled ? 'disabled' : ''}>
            {user.email}
          </Typography>
        );
      },
    },
    {
      key: 'lastActive',
      label: 'Last active',
      width: '120px',
      render: (user: User) => {
        const isDisabled = user.status === 'suspended' || user.status === 'pending';
        const isPending = user.lastActive === 'Pending';
        const classNames = [];
        if (isPending) classNames.push('users-table-last-active pending');
        if (isDisabled && !isPending) classNames.push('disabled');
        
        return (
          <Typography className={classNames.join(' ')}>
            {user.lastActive}
          </Typography>
        );
      },
    },
    {
      key: 'status',
      label: '',
      width: '120px',
      align: 'right',
      render: (user: User) => {
        return user.status ? <StatusChip status={user.status} /> : null;
      },
    },
    {
      key: 'actions',
      label: '',
      width: '56px',
      align: 'center',
      render: (user: User) => (
        <IconButton 
          className="users-table-action-button"
          onClick={(e) => handleMenuOpen(e, user)}
        >
          <MoreHorizIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box className="users-table-wrapper">
      <Table
        columns={columns}
        data={paginatedUsers}
        keyExtractor={(user) => user.adminId}
        emptyMessage="No users found"
      />
      
      {showPagination && users.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={users.length}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          itemLabel="users"
        />
      )}

      {/* Action Dropdown */}
      <Dropdown
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        options={getDropdownOptions(selectedUser)}
      />
    </Box>
  );
};

export default UsersTable;
